/** Shared helpers for email attachment pick/compress/size checks. */

export const MAX_ATTACHMENT_BYTES = 4 * 1024 * 1024
export const MAX_TOTAL_ATTACHMENT_BYTES = 8 * 1024 * 1024

export const ALLOWED_IMAGE_MIMES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif'
]

export const ALLOWED_MIMES = ALLOWED_IMAGE_MIMES.concat(['application/pdf'])

const IMAGE_MAX_EDGE = 1600
const JPEG_QUALITY = 0.7

export function isImageMime (type) {
  const t = String(type || '').toLowerCase()
  return t.indexOf('image/') === 0
}

export function isAllowedMime (type) {
  const t = String(type || '').toLowerCase()
  if (ALLOWED_MIMES.indexOf(t) !== -1) return true
  // Some browsers report empty type for HEIC/unknown — reject those
  return false
}

export function formatFileSize (bytes) {
  const n = Number(bytes)
  if (!Number.isFinite(n) || n < 0) return ''
  if (n < 1024) return n + ' B'
  if (n < 1024 * 1024) {
    const kb = n / 1024
    return (kb < 10 ? kb.toFixed(1) : Math.round(kb)) + ' KB'
  }
  const mb = n / (1024 * 1024)
  return (mb < 10 ? mb.toFixed(1) : Math.round(mb)) + ' MB'
}

export function fileToBase64 (blob) {
  return new Promise(function (resolve, reject) {
    const reader = new FileReader()
    reader.onload = function () {
      const result = String(reader.result || '')
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.onerror = function () {
      reject(reader.error || new Error('Failed to read file'))
    }
    reader.readAsDataURL(blob)
  })
}

function loadImageFromFile (file) {
  return new Promise(function (resolve, reject) {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = function () {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = function () {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load image'))
    }
    img.src = url
  })
}

/**
 * Resize/compress an image file. Returns a Blob (JPEG for most images; PNG kept if small gif/png with transparency needs fail — we use JPEG for photos).
 * GIFs are left as-is (canvas would drop animation).
 */
export async function compressImage (file) {
  const type = String(file.type || '').toLowerCase()
  if (type === 'image/gif') {
    return file
  }
  const img = await loadImageFromFile(file)
  let width = img.naturalWidth || img.width
  let height = img.naturalHeight || img.height
  if (!width || !height) return file

  const maxEdge = Math.max(width, height)
  if (maxEdge > IMAGE_MAX_EDGE) {
    const scale = IMAGE_MAX_EDGE / maxEdge
    width = Math.round(width * scale)
    height = Math.round(height * scale)
  }

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return file
  ctx.drawImage(img, 0, 0, width, height)

  const blob = await new Promise(function (resolve) {
    canvas.toBlob(function (b) {
      resolve(b)
    }, 'image/jpeg', JPEG_QUALITY)
  })
  if (!blob || blob.size >= file.size) {
    return file
  }
  return blob
}

function thumbnailFromBlob (blob, widthPx) {
  return new Promise(function (resolve) {
    const url = URL.createObjectURL(blob)
    const img = new Image()
    img.onload = function () {
      const w = widthPx || 24
      const scale = w / (img.naturalWidth || img.width || w)
      const h = Math.max(1, Math.round((img.naturalHeight || img.height || w) * scale))
      const canvas = document.createElement('canvas')
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      if (ctx) ctx.drawImage(img, 0, 0, w, h)
      URL.revokeObjectURL(url)
      try {
        resolve(canvas.toDataURL('image/jpeg', 0.8))
      } catch (e) {
        resolve(url)
      }
    }
    img.onerror = function () {
      URL.revokeObjectURL(url)
      resolve('')
    }
    img.src = url
  })
}

function renameToJpeg (name) {
  const base = String(name || 'image').replace(/\.[^.]+$/, '')
  return base + '.jpg'
}

/**
 * Process a user-selected File into an attachment payload object.
 * @returns {Promise<{id, kind, filename, type, size, content, thumbnail, sizeLabel}>}
 */
export async function processUserFile (file) {
  const originalType = String(file.type || '').toLowerCase()
  if (!isAllowedMime(originalType)) {
    throw new Error('Only images and PDF files can be attached')
  }
  if (originalType === 'application/pdf') {
    if (file.size > MAX_ATTACHMENT_BYTES) {
      throw new Error('Each file must be under 4 MB')
    }
    const content = await fileToBase64(file)
    return {
      id: 'user-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
      kind: 'user',
      filename: file.name || 'document.pdf',
      type: 'application/pdf',
      size: file.size,
      content: content,
      thumbnail: '',
      sizeLabel: formatFileSize(file.size)
    }
  }

  const compressed = await compressImage(file)
  const outType = compressed.type || (compressed !== file ? 'image/jpeg' : originalType)
  const outName = outType === 'image/jpeg' && originalType !== 'image/jpeg'
    ? renameToJpeg(file.name)
    : (file.name || 'image.jpg')
  if (compressed.size > MAX_ATTACHMENT_BYTES) {
    throw new Error('Each file must be under 4 MB after compression')
  }
  const content = await fileToBase64(compressed)
  const thumbnail = await thumbnailFromBlob(compressed, 24)
  return {
    id: 'user-' + Date.now() + '-' + Math.random().toString(36).slice(2, 8),
    kind: 'user',
    filename: outName,
    type: outType,
    size: compressed.size,
    content: content,
    thumbnail: thumbnail,
    sizeLabel: formatFileSize(compressed.size)
  }
}

export function sumAttachmentBytes (items) {
  return (items || []).reduce(function (sum, item) {
    const n = Number(item && item.size)
    return sum + (Number.isFinite(n) ? n : 0)
  }, 0)
}

export function wouldExceedTotal (existingItems, newSize) {
  return sumAttachmentBytes(existingItems) + (Number(newSize) || 0) > MAX_TOTAL_ATTACHMENT_BYTES
}
